import React, { useEffect, useState } from 'react'
import { Drawer, Switch, InputNumber, Button } from 'antd'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import './PointsSideModal.scss'
import { api } from '../../../../../../../../services'
import { useTranslation } from 'react-i18next'

const defaultPointsState = {
  points_enabled: false,
  point_rules: [
    { action_type: 'login', is_enabled: false, point_value: 0 },
    { action_type: 'quiz_complete', is_enabled: false, point_value: 0 },
    { action_type: 'lesson_complete', is_enabled: false, point_value: 0 },
    { action_type: 'get_certificate', is_enabled: false, point_value: 0 },
    { action_type: 'tip_approved', is_enabled: false, point_value: 0 },
    { action_type: 'tip_liked', is_enabled: false, point_value: 0 }
  ]
}

const defaultExpanded = {
  general: true,
  courses: true,
  others: true
}

const PointsSideModal = ({ visible, onClose, data, refreshData }) => {
  const [state, setState] = useState(defaultPointsState)
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [isLoading, setIsLoading] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (data && (!data.point_rules || data.point_rules.length === 0)) {
      // If data has no point_rules or empty array, use default point_rules
      setState({
        ...data,
        point_rules: defaultPointsState.point_rules
      })
    } else {
      setState(data)
    }
  }, [data])
  // Helper functions to work with point_rules
  const getPointRule = actionType => {
    return state.point_rules.find(rule => rule.action_type === actionType)
  }

  const updatePointRule = (actionType, field, value) => {
    setState(prev => ({
      ...prev,
      point_rules: prev.point_rules.map(rule =>
        rule.action_type === actionType ? { ...rule, [field]: value } : rule
      )
    }))
  }

  const handleSwitch = key => setState(prev => ({ ...prev, [key]: !prev[key] }))
  const toggleSection = key =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  const updateGlobalSettings = async () => {
    let data = {
      points_enabled: state.points_enabled,
      point_rules: state.point_rules
    }
    setIsLoading(true)
    try {
      await api.leaderboard.updateGlobalSettings(data)
      onClose()
      refreshData()
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }
  return (
    <Drawer
      title={<span className="points-modal__title">{t('v4:points')}</span>}
      placement="right"
      width={500}
      closable={true}
      onClose={onClose}
      visible={visible}
      maskStyle={{ background: 'rgba(0,0,0,0.15)' }}
      bodyStyle={{ padding: 0 }}
    >
      <div className="points-side-modal">
        <div className="points-modal__container">
          <div className="points-modal__header">
            <Switch
              checked={state.points_enabled}
              onChange={() => handleSwitch('points_enabled')}
            />
            <div className="points-modal__header-title-container">
              <span className="points-modal__header-title">
                {t('v4:points_enabled')}
              </span>
              <span className="points-modal__header-desc">
                {t('v4:learning_activities_and_encourage_healthy_competition')}
              </span>
            </div>
          </div>
          <div className="points-modal__section-title">
            {t('v4:award_points')}
          </div>
          <div className="points-modal__section-desc">
            {t('v4:the_following_equates_the_value_of_one_item_completion')}
          </div>

          {/* General Section */}
          <div className="points-modal__expandable-section">
            <div
              className="points-modal__expandable-header"
              onClick={() => toggleSection('general')}
            >
              {expanded.general ? (
                <CaretDownOutlined />
              ) : (
                <CaretRightOutlined />
              )}{' '}
              <span>{t('v4:general')}</span>
            </div>
            {expanded.general && (
              <div className="points-modal__expandable-content">
                <div className="points-modal__switch-row">
                  <div className="points-modal__switch-label-group">
                    <Switch
                      checked={getPointRule('login')?.is_enabled || false}
                      onChange={() =>
                        updatePointRule(
                          'login',
                          'is_enabled',
                          !getPointRule('login')?.is_enabled
                        )
                      }
                    />
                    <span className="points-modal__switch-label">
                      {t('v3:login')}
                    </span>
                  </div>
                  <InputNumber
                    className="points-modal__input"
                    min={0}
                    placeholder="e.g. 25"
                    value={getPointRule('login')?.point_value || 0}
                    onChange={value =>
                      updatePointRule('login', 'point_value', value)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* Courses Section */}
          <div className="points-modal__expandable-section">
            <div
              className="points-modal__expandable-header"
              onClick={() => toggleSection('courses')}
            >
              {expanded.courses ? (
                <CaretDownOutlined />
              ) : (
                <CaretRightOutlined />
              )}{' '}
              <span>{t('reports:courses')}</span>
            </div>
            {expanded.courses && (
              <div className="points-modal__expandable-content">
                <div className="points-modal__switch-row">
                  <div className="points-modal__switch-label-group">
                    <Switch
                      checked={
                        getPointRule('lesson_complete')?.is_enabled || false
                      }
                      onChange={() =>
                        updatePointRule(
                          'lesson_complete',
                          'is_enabled',
                          !getPointRule('lesson_complete')?.is_enabled
                        )
                      }
                    />
                    <span className="points-modal__switch-label">
                      {t('general:lesson')}
                    </span>
                  </div>
                  <InputNumber
                    className="points-modal__input"
                    min={0}
                    placeholder="e.g. 25"
                    value={getPointRule('lesson_complete')?.point_value || 0}
                    onChange={value =>
                      updatePointRule('lesson_complete', 'point_value', value)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* Others Section */}
          <div className="points-modal__expandable-section">
            <div
              className="points-modal__expandable-header"
              onClick={() => toggleSection('others')}
            >
              {expanded.others ? <CaretDownOutlined /> : <CaretRightOutlined />}{' '}
              <span>{t('v4:others')}</span>
            </div>
            {expanded.others && (
              <div className="points-modal__expandable-content">
                <div className="points-modal__switch-row">
                  <div className="points-modal__switch-label-group">
                    <Switch
                      checked={
                        getPointRule('get_certificate')?.is_enabled || false
                      }
                      onChange={() =>
                        updatePointRule(
                          'get_certificate',
                          'is_enabled',
                          !getPointRule('get_certificate')?.is_enabled
                        )
                      }
                    />
                    <span className="points-modal__switch-label">
                      {t('v4:certificate')}
                    </span>
                  </div>
                  <InputNumber
                    className="points-modal__input"
                    min={0}
                    placeholder="e.g. 25"
                    value={getPointRule('get_certificate')?.point_value || 0}
                    onChange={value =>
                      updatePointRule('get_certificate', 'point_value', value)
                    }
                  />
                </div>
                <div className="points-modal__switch-row">
                  <div className="points-modal__switch-label-group">
                    <Switch
                      checked={
                        getPointRule('quiz_complete')?.is_enabled || false
                      }
                      onChange={() =>
                        updatePointRule(
                          'quiz_complete',
                          'is_enabled',
                          !getPointRule('quiz_complete')?.is_enabled
                        )
                      }
                    />
                    <span className="points-modal__switch-label">
                      {t('v4:quiz_passed')}
                    </span>
                  </div>
                  <InputNumber
                    className="points-modal__input"
                    min={0}
                    placeholder="e.g. 25"
                    value={getPointRule('quiz_complete')?.point_value || 0}
                    onChange={value =>
                      updatePointRule('quiz_complete', 'point_value', value)
                    }
                  />
                </div>
                <div className="points-modal__switch-row">
                  <div className="points-modal__switch-label-group">
                    <Switch
                      checked={
                        getPointRule('tip_approved')?.is_enabled || false
                      }
                      onChange={() =>
                        updatePointRule(
                          'tip_approved',
                          'is_enabled',
                          !getPointRule('tip_approved')?.is_enabled
                        )
                      }
                    />
                    <span className="points-modal__switch-label">
                      {t('v4:tip_approved')}
                    </span>
                  </div>
                  <InputNumber
                    className="points-modal__input"
                    min={0}
                    placeholder="e.g. 25"
                    value={getPointRule('tip_approved')?.point_value || 0}
                    onChange={value =>
                      updatePointRule('tip_approved', 'point_value', value)
                    }
                  />
                </div>
                <div className="points-modal__switch-row">
                  <div className="points-modal__switch-label-group">
                    <Switch
                      checked={getPointRule('tip_liked')?.is_enabled || false}
                      onChange={() =>
                        updatePointRule(
                          'tip_liked',
                          'is_enabled',
                          !getPointRule('tip_liked')?.is_enabled
                        )
                      }
                    />
                    <span className="points-modal__switch-label">
                      {t('v4:tip_liked')}
                    </span>
                  </div>
                  <InputNumber
                    className="points-modal__input"
                    min={0}
                    placeholder="e.g. 25"
                    value={getPointRule('tip_liked')?.point_value || 0}
                    onChange={value =>
                      updatePointRule('tip_liked', 'point_value', value)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className="points-modal__footer">
            <Button
              className="points-modal__discard"
              onClick={onClose}
              disabled={isLoading}
            >
              {t('v4:discard')}
            </Button>
            <Button
              type="primary"
              className="points-modal__save"
              loading={isLoading}
              onClick={updateGlobalSettings}
            >
              {t('buttons:save')}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default PointsSideModal
